import { Tooltip } from '@mui/material';
import { useRef, KeyboardEvent } from 'react';
import './biz-tabs.scss';

export type BizTab = {
	key: string;
	title: string;
	icon?: string;
	disabled?: boolean;
	selected?: boolean;
	visible?: boolean;
};

type BizTabsProps = {
	tabs: BizTab[];
	selectedTabKey: string;
	onClick: (key: string) => void;
	ariaLabel?: string;
	variant?: 'default' | 'admin';
	appearance?: 'default' | 'rounded';
	size?: 'normal' | 'small';
	tabPosition?: 'start' | 'spread' | 'end';
};

const BizTabs = ({
	tabs,
	selectedTabKey,
	onClick,
	ariaLabel,
	variant = 'default',
	appearance = 'default',
	size = 'normal',
	tabPosition = 'start',
}: BizTabsProps) => {
	const tabsRef = useRef<(HTMLButtonElement | null)[]>([]);

	const visibleTabs = tabs.filter((tab) => tab.visible !== false);

	const handleKeyDown = (e: KeyboardEvent<HTMLButtonElement>, currentIndex: number) => {
		const enabledTabs = visibleTabs.map((tab, index) => ({ tab, index })).filter(({ tab }) => !tab.disabled);

		if (enabledTabs.length === 0) return;

		const currentEnabledIndex = enabledTabs.findIndex(({ index }) => index === currentIndex);
		let nextIndex = -1;

		switch (e.key) {
			case 'ArrowRight':
			case 'ArrowDown':
				e.preventDefault();
				nextIndex = enabledTabs[(currentEnabledIndex + 1) % enabledTabs.length].index;
				break;
			case 'ArrowLeft':
			case 'ArrowUp':
				e.preventDefault();
				nextIndex = enabledTabs[(currentEnabledIndex - 1 + enabledTabs.length) % enabledTabs.length].index;
				break;
			case 'Home':
				e.preventDefault();
				nextIndex = enabledTabs[0].index;
				break;
			case 'End':
				e.preventDefault();
				nextIndex = enabledTabs[enabledTabs.length - 1].index;
				break;
		}

		if (nextIndex >= 0) {
			tabsRef.current[nextIndex]?.focus();
			onClick(visibleTabs[nextIndex].key);
		}
	};

	if (variant === 'admin') {
		return (
			<div className={`admin-tabs ${size === 'small' ? 'small' : ''} tab-pos-${tabPosition}`}>
				<ul className='nav nav-tabs'>
					{visibleTabs.map((tab) => (
						<li key={tab.key} className={selectedTabKey === tab.key ? 'active' : ''}>
							<a onClick={() => onClick(tab.key)}>{tab.title}</a>
						</li>
					))}
				</ul>
			</div>
		);
	}

	return (
		<div className={`biz-tab-group appearance-${appearance} tab-pos-${tabPosition}`} role='tablist' aria-label={ariaLabel}>
			{visibleTabs.map((tab, index) => {
				const isSelected = tab.key === selectedTabKey;
				return (
					<button
						key={tab.key}
						ref={(el) => (tabsRef.current[index] = el)}
						role='tab'
						aria-selected={isSelected}
						aria-disabled={tab.disabled || undefined}
						tabIndex={isSelected ? 0 : -1}
						className={`tab ${isSelected ? 'active' : ''} ${tab.disabled ? 'disabled' : ''}`}
						onClick={() => {
							if (!tab.disabled && onClick) {
								onClick(tab.key);
							}
						}}
						onKeyDown={(e) => handleKeyDown(e, index)}
						disabled={tab.disabled}
					>
						{tab.icon && (
							<Tooltip title={tab.title} arrow>
								<i className={`tab-icon ${tab.icon}`} aria-hidden='true'></i>
							</Tooltip>
						)}
						<span className='tab-label'>{tab.title}</span>
					</button>
				);
			})}
		</div>
	);
};

export default BizTabs;
